/**
 * ConflictOverviewModal.jsx
 * Central hub for reviewing and managing assignment conflicts.
 * Refactored V5: Modularized into sub-components and hooks.
 */
/* eslint-disable */
import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import './ConflictOverview.css';
import { useConflictState } from './hooks/useConflictState';
import { useSafeClose } from '../../../../hooks/useSafeClose';

// Sub-components
import ConflictHeader from './components/ConflictHeader';
import ConflictFilterBar from './components/ConflictFilterBar';
import ConflictContent from './components/ConflictContent';

/**
 * ConflictOverviewModal Component
 * A high-density dashboard for managing resource conflicts across workers and vehicles.
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is visible
 * @param {Function} props.onClose - Callback to close the modal
 * @param {Array} props.conflicts - List of pre-calculated conflicts from LeaderPage
 * @param {Array} props.workers - Reference list of all workers
 * @param {Array} props.vehicles - Reference list of all vehicles
 * @param {Array} props.kolonnen - Reference list of all teams (kolonnen)
 * @param {Array} props.assignments - Reference list of all assignments
 * @param {boolean} props.isDetectionEnabled - Current state of global conflict detection
 * @param {Function} props.onToggleDetection - Callback to switch detection on/off
 * @param {Function} props.onEditAssignment - Callback to open the assignment editor
 * @param {Function} props.onEditResource - Callback to open the resource/absence editor
 */
const ConflictOverviewModal = ({
    isOpen,
    onClose,
    conflicts = [],
    workers = [],
    vehicles = [],
    isDetectionEnabled = true,
    onToggleDetection,
    onEditAssignment,
    onEditResource,
    kolonnen = [],
    assignments = [],
    targetResource = null, // { type: 'worker'|'vehicle'|'kolonne', id: string }
    expandedResources: externalExpanded = null,
    onExpandedResourcesChange = null,
    activeTab: externalActiveTab = 'resources',
    onActiveTabChange = null,
    collapsedSections = new Set(['kolonnen', 'mitarbeiter', 'fahrzeuge']),
    onCollapsedSectionsChange = null
}) => {
    const [dateMenuStyles, setDateMenuStyles] = useState({ left: '50%', transform: 'translateX(-50%)' });
    const [reasonMenuStyles, setReasonMenuStyles] = useState({ left: '50%', transform: 'translateX(-50%)' });
    
    const datePickerRef = useRef(null);
    const reasonFilterRef = useRef(null);
    const resTypeFilterRef = useRef(null);

    const { overlayRef, onMouseDown, onMouseUp } = useSafeClose(onClose);

    if (!isOpen) return null;

    const {
        activeTab, setActiveTab,
        search, setSearch,
        reasonFilter, setReasonFilter,
        resTypeFilter, setResTypeFilter,
        dateRange, setDateRange,
        sortConfig, toggleSort,
        expandedResources, toggleExpand,
        setExpandedResources,
        activeDropdown, setActiveDropdown,
        allReasons,
        logicalCount,
        kolonneGroups,
        personGroups,
        vehicleGroups,
        allFlattened,
        toggleReason,
        toggleResType,
        formatReason,
        resetFilters,
        hasActiveFilters
    } = useConflictState({ 
        conflicts, 
        workers, 
        isDetectionEnabled, 
        kolonnen,
        initialFocusResource: targetResource,
        expandedResources: externalExpanded,
        onExpandedChange: onExpandedResourcesChange,
        activeTab: externalActiveTab,
        onActiveTabChange: onActiveTabChange
    });

    const toggleSection = (sectionId) => {
        const next = new Set(collapsedSections);
        if (next.has(sectionId)) next.delete(sectionId);
        else next.add(sectionId);
        onCollapsedSectionsChange?.(next);
    };

    // Initial targeting logic
    useEffect(() => {
        if (targetResource?.type) {
            const nextCollapsed = new Set(['kolonnen', 'mitarbeiter', 'fahrzeuge']);
            const mapping = { worker: 'mitarbeiter', vehicle: 'fahrzeuge', kolonne: 'kolonnen' };
            const targetId = mapping[targetResource.type];
            if (targetId) nextCollapsed.delete(targetId);
            onCollapsedSectionsChange?.(nextCollapsed);
        }
    }, [targetResource]);

    // Smart positioning for dropdowns
    useLayoutEffect(() => {
        const updatePosition = (anchor, setter) => {
            if (anchor) {
                const container = anchor.closest('.co-modal-container');
                if (container) {
                    const anchorRect = anchor.getBoundingClientRect();
                    const containerRect = container.getBoundingClientRect();
                    const anchorCenter = anchorRect.left + (anchorRect.width / 2);
                    const containerCenter = containerRect.left + (containerRect.width / 2);

                    if (anchorCenter > containerCenter + 100) {
                        setter({ right: 0, left: 'auto', transform: 'none' });
                    } else if (anchorCenter < containerCenter - 100) {
                        setter({ left: 0, right: 'auto', transform: 'none' });
                    } else {
                        setter({ left: '50%', transform: 'translateX(-50%)' });
                    }
                }
            }
        };

        if (activeDropdown === 'date') updatePosition(datePickerRef.current, setDateMenuStyles);
        if (activeDropdown === 'reason') updatePosition(reasonFilterRef.current, setReasonMenuStyles);
    }, [activeDropdown]);

    // Click outside listener for dropdowns
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (!activeDropdown) return;
            
            const refs = {
                resType: resTypeFilterRef,
                date: datePickerRef,
                reason: reasonFilterRef
            };
            
            const currentRef = refs[activeDropdown];
            if (currentRef?.current && !currentRef.current.contains(e.target)) {
                setActiveDropdown(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [activeDropdown, setActiveDropdown]);

    const handleExpandAll = () => {
        // Expand all sections
        onCollapsedSectionsChange?.(new Set());

        // Expand all resource cards
        const allGids = new Set();
        kolonneGroups.forEach(g => allGids.add(`kolonne-${g.id}`));
        personGroups.forEach(g => allGids.add(`worker-${g.id}`));
        vehicleGroups.forEach(g => allGids.add(`vehicle-${g.id}`));
        setExpandedResources(allGids);
    };

    const handleCollapseAll = () => {
        // Collapse all sections
        onCollapsedSectionsChange?.(new Set(['kolonnen', 'mitarbeiter', 'fahrzeuge']));

        // Collapse all resource cards
        setExpandedResources(new Set());
    };

    const sections = [
        { id: 'kolonnen', title: 'KOLONNEN', groups: kolonneGroups, icon: '👥' },
        { id: 'mitarbeiter', title: 'MITARBEITER', groups: personGroups, icon: '👤' },
        { id: 'fahrzeuge', title: 'FAHRZEUGE', groups: vehicleGroups, icon: '🚛' }
    ];

    return (
        <div className="co-modal-overlay" ref={overlayRef} onMouseDown={onMouseDown} onMouseUp={onMouseUp}>
            <div className="co-modal-container" onClick={(e) => e.stopPropagation()}>
                
                <ConflictHeader 
                    isDetectionEnabled={isDetectionEnabled}
                    logicalCount={logicalCount}
                    onToggleDetection={onToggleDetection}
                    onClose={onClose}
                />

                <div className="co-tabs">
                    <div
                        className={`co-tab ${activeTab === 'resources' ? 'active' : ''}`}
                        onClick={() => setActiveTab('resources')}
                    >
                        Ressourcen
                    </div>
                    <div
                        className={`co-tab ${activeTab === 'list' ? 'active' : ''}`}
                        onClick={() => setActiveTab('list')}
                    >
                        Liste
                    </div>
                    <div
                        className={`co-tab ${activeTab === 'archive' ? 'active' : ''}`}
                        onClick={() => setActiveTab('archive')}
                    >
                        Archiv
                    </div>
                </div>
                
                <ConflictFilterBar 
                    isDetectionEnabled={isDetectionEnabled}
                    activeTab={activeTab}
                    search={search}
                    setSearch={setSearch}
                    resTypeFilter={resTypeFilter}
                    toggleResType={toggleResType}
                    setResTypeFilter={setResTypeFilter}
                    dateRange={dateRange}
                    setDateRange={setDateRange}
                    reasonFilter={reasonFilter}
                    toggleReason={toggleReason}
                    setReasonFilter={setReasonFilter}
                    allReasons={allReasons}
                    activeDropdown={activeDropdown}
                    setActiveDropdown={setActiveDropdown}
                    datePickerRef={datePickerRef}
                    reasonFilterRef={reasonFilterRef}
                    resTypeFilterRef={resTypeFilterRef}
                    dateMenuStyles={dateMenuStyles}
                    reasonMenuStyles={reasonMenuStyles}
                    handleExpandAll={handleExpandAll}
                    handleCollapseAll={handleCollapseAll}
                    formatReason={formatReason}
                    resetFilters={resetFilters}
                    hasActiveFilters={hasActiveFilters}
                />

                <ConflictContent 
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    isDetectionEnabled={isDetectionEnabled}
                    sections={sections}
                    expandedResources={expandedResources}
                    toggleExpand={toggleExpand}
                    collapsedSections={collapsedSections}
                    toggleSection={toggleSection}
                    onEditAssignment={onEditAssignment}
                    onEditResource={onEditResource}
                    formatReason={formatReason}
                    allFlattened={allFlattened}
                    sortConfig={sortConfig}
                    toggleSort={toggleSort}
                />

            </div>
        </div>
    );
};

export default ConflictOverviewModal;
